/**
 * Date formatting utilities for Belgian date format (DD-MM-YYYY)
 */

/**
 * Format a date string to DD-MM-YYYY format
 * @param dateStr - Date string in ISO format (YYYY-MM-DD) or Date object
 * @param options - Optional formatting options
 * @param options.includeYear - Include year in output (default true)
 * @param options.longMonth - Use long month name
 * @param options.includeWeekday - Include weekday name
 * @returns Formatted date string in DD-MM-YYYY format
 */
export function formatDate(dateStr: string | Date | null | undefined, options?: {
  includeYear?: boolean
  longMonth?: boolean
  includeWeekday?: boolean
}): string {
  if (!dateStr)
    return '-'

  try {
    const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr

    // Check for invalid date
    if (Number.isNaN(date.getTime()))
      return '-'

    if (options?.includeWeekday) {
      return date.toLocaleDateString('nl-BE', {
        weekday: 'long',
        day: '2-digit',
        month: options.longMonth ? 'long' : '2-digit',
        year: options.includeYear !== false ? 'numeric' : undefined,
      })
    }

    if (options?.longMonth) {
      return date.toLocaleDateString('nl-BE', {
        day: 'numeric',
        month: 'long',
        year: options.includeYear !== false ? 'numeric' : undefined,
      })
    }

    // Default format: DD-MM-YYYY
    return date.toLocaleDateString('nl-BE', {
      day: '2-digit',
      month: '2-digit',
      year: options?.includeYear !== false ? 'numeric' : undefined,
    })
  }
  catch (error) {
    console.error('Error formatting date:', error)

    return '-'
  }
}

/**
 * Format a date to include both date and time
 * @param dateStr - Date string in ISO format
 * @returns Formatted date string in DD-MM-YYYY HH:mm format
 */
export function formatDateTime(dateStr: string | Date | null | undefined): string {
  try {
    if (!dateStr)
      return '-'

    const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr

    if (Number.isNaN(date.getTime()))
      return '-'

    return date.toLocaleString('nl-BE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }
  catch (error) {
    console.error('Error formatting datetime:', error)

    return '-'
  }
}

/**
 * Convert DD-MM-YYYY format to YYYY-MM-DD (ISO format for database)
 * @param dateStr - Date string in DD-MM-YYYY format
 * @returns Date string in YYYY-MM-DD format
 */
export function convertToISODate(dateStr: string): string {
  if (!dateStr)
    return ''

  // If already in YYYY-MM-DD format, return as is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr))
    return dateStr

  // If in DD-MM-YYYY format, convert it
  if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
    const [day, month, year] = dateStr.split('-')

    return `${year}-${month}-${day}`
  }

  // If in DD/MM/YYYY format, convert it
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
    const [day, month, year] = dateStr.split('/')

    return `${year}-${month}-${day}`
  }

  return dateStr
}

/**
 * Convert YYYY-MM-DD (ISO format) to DD-MM-YYYY format
 * @param dateStr - Date string in YYYY-MM-DD format
 * @returns Date string in DD-MM-YYYY format
 */
export function convertFromISODate(dateStr: string): string {
  if (!dateStr)
    return ''

  // If already in DD-MM-YYYY format, return as is
  if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr))
    return dateStr

  // If in YYYY-MM-DD format, convert it
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split('-')

    return `${day}-${month}-${year}`
  }

  return dateStr
}

/**
 * Get flatpickr config for date pickers with Belgian format
 */
export function getDatePickerConfig(options?: {
  enableTime?: boolean
  maxDate?: Date
  minDate?: Date
}) {
  return {
    dateFormat: options?.enableTime ? 'd-m-Y H:i' : 'd-m-Y',
    time_24hr: true,
    maxDate: options?.maxDate,
    minDate: options?.minDate,
  }
}
